import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { reassignInvoice, reassignQuote, reassignContract, reassignSensor, reassignTransmitter } from '../api';
import type { ReassignResult } from '../api';

type EntityType = 'invoice' | 'quote' | 'contract' | 'sensor' | 'transmitter';

const reassignFns = {
  invoice: reassignInvoice,
  quote: reassignQuote,
  contract: reassignContract,
  sensor: reassignSensor,
  transmitter: reassignTransmitter,
} as const;

const queryKeyMap = {
  invoice: 'account-invoices',
  quote: 'account-quotes',
  contract: 'account-contracts',
  sensor: 'account-sensors',
  transmitter: 'account-transmitters',
} as const;

export function useReassignEntity(entityType: EntityType, accountId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, targetAccountId }: { entityId: string; targetAccountId: string }) =>
      reassignFns[entityType](entityId, targetAccountId),
    onSuccess: (_data: ReassignResult, variables) => {
      toast.success(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} reassigned successfully`);
      // Invalidate source account queries
      queryClient.invalidateQueries({ queryKey: [queryKeyMap[entityType], accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      // Invalidate target account queries so the entity appears there
      queryClient.invalidateQueries({ queryKey: [queryKeyMap[entityType], variables.targetAccountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', variables.targetAccountId] });
      // Invalidate operations-level list queries
      if (entityType === 'sensor') {
        queryClient.invalidateQueries({ queryKey: ['sensors'] });
      }
      if (entityType === 'transmitter') {
        // Transmitter reassignment cascades to streams and sensors
        queryClient.invalidateQueries({ queryKey: ['account-sensors', accountId] });
        queryClient.invalidateQueries({ queryKey: ['account-sensors', variables.targetAccountId] });
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to reassign ${entityType}: ${error.message}`);
    },
  });
}
